import React, { useEffect, useState, useCallback } from "react";
import { api as axios, authFetch } from "../config/apiConfig";
import { useNavigate } from "react-router-dom";
import AddRateCardItemModal from "./AddRateCardItemModal";
import { API_ORIGIN } from "../config/apiConfig";
import { ROUTES } from "../config/routes";
import { printProjectReport } from "../utils/printProjectReport";

const API_BASE = API_ORIGIN;

// Same step indicator as Step1/Step3. See the comment above Step1's
// WIZARD_STEPS for why the tabs themselves aren't real links.
const WIZARD_STEPS = [
  { key: "project", icon: "ti-file-description", label: "Project", sub: "Info & Overview" },
  { key: "budget", icon: "ti-currency-riyal", label: "Budget", sub: "Amount & Summary" },
  { key: "asset", icon: "ti-building", label: "Asset", sub: "Selection & Element" },
  { key: "element", icon: "ti-list-check", label: "Element", sub: "Main & Sub Element" },
  { key: "estimation", icon: "ti-report-money", label: "Estimation", sub: "Amount & Summary" },
  { key: "basis", icon: "ti-clipboard-text", label: "Basis of Cost", sub: "Assumption & Exclusion" },
  { key: "construction", icon: "ti-hammer", label: "Construction", sub: "Scope" },
];

// Which of the 7 wizard steps this PAGE is. Steps before this one get
// the theme's own light-green "done" tint (.wizard-item-done -- built
// into app.min.css, so this part always rendered correctly); THIS step
// gets the solid brand-green "current" highlight (.wizard-item-current,
// defined in src/styles/themeOverrides.css -- see Layout.jsx for why
// that file, not appLayout.css, is what actually loads it); steps after
// this one stay inert (.disabled).
const CURRENT_STEP_KEY = "element";
const CURRENT_STEP_INDEX = WIZARD_STEPS.findIndex((s) => s.key === CURRENT_STEP_KEY);

// Read-only counterpart: every wizard tab links to that step's own
// read-only "view" page (never the real editable Step1-7 pages), so
// browsing a finished project's steps from here can never reach the
// editable wizard flow. See routes.js for why these are separate
// route constants from the editable COST_ESTIMATION_STEP* ones.
const STEP_VIEW_ROUTES = {
  project: ROUTES.COST_ESTIMATION_STEP1_VIEW,
  budget: ROUTES.COST_ESTIMATION_STEP2_VIEW,
  asset: ROUTES.COST_ESTIMATION_STEP3_VIEW,
  element: ROUTES.COST_ESTIMATION_STEP4_VIEW,
  estimation: ROUTES.COST_ESTIMATION_STEP5_VIEW,
  basis: ROUTES.BASIS_OF_COST_VIEW,
  construction: ROUTES.ELEMENT_SCOPE_PAGE1_VIEW,
};

function Step4SubElementReadOnly() {
  const navigate = useNavigate();

  // Fix for the Quantity-not-restoring bug: this used to be a plain empty
  // default, filled in later by a useEffect that read localStorage("step3")
  // after mount. That created an extra render where step3.projectID was
  // still "" -- and the savedRowsByGroup-loading effect below (keyed off
  // step3.projectID) would see that empty "" first, take its "nothing to
  // load yet" branch, and set savedRowsLoaded=true immediately (with an
  // empty map). By the time the REAL projectID arrived a render later, the
  // eager-preload effect saw savedRowsLoaded already true and fired
  // instantly -- calling loadSubElements for every Main Element BEFORE the
  // real GET /get_project_sub_elements fetch (only just started in that
  // same pass) had a chance to resolve. Every Main Element fell through to
  // the Rate Card default (Quantity: 1), and since loadSubElements skips
  // any mainKey it already has data for, the correct saved rows -- which
  // arrived a moment later -- were never applied. This is why Quantity
  // reset to 1 every single time, 100% reproducibly, with no code path
  // left to self-correct it.
  //
  // Reading localStorage synchronously here (a lazy useState initializer,
  // not an effect) means the very first render already has the real
  // projectID -- so the savedRowsByGroup effect's FIRST run uses the real
  // id and genuinely waits for its fetch to resolve before savedRowsLoaded
  // ever becomes true, and the eager-preload effect can no longer race
  // ahead of it.
  // FIX: this page used to have NO fallback at all when localStorage
  // "step3" was missing -- that key is only ever written by
  // Step3AssetSelectionReadOnly.jsx's own "Next" click. Arriving here
  // directly from the Executive Summary's (or Dashboard's) wizard tabs --
  // or any other entry that skips the "Asset" step first in this browser
  // session -- left step3.rows permanently empty, so this whole page
  // rendered nothing at all ("Cost of main elements = 0.00", no Asset
  // Group/Main Element sections whatsoever) even though the project's
  // selection was genuinely saved in the database. A setter is added back
  // below (was none before) so a DB fallback fetched after mount, a few
  // lines down, can fill this in -- see dbFallbackPending right after.
  const [step3, setStep3] = useState(() => {
    const empty = { projectName: "", projectID: "", C_ID: "", operatingCompany: "", dataYears: [], rows: [] };
    try {
      const raw = localStorage.getItem("step3");
      if (!raw) return empty;
      const parsed = JSON.parse(raw);
      return {
        projectName: parsed.projectName ?? parsed.project_name ?? "",
        projectID: parsed.projectID ?? parsed.project_id ?? "",
        C_ID: parsed.C_ID ?? parsed.c_id ?? "",
        operatingCompany: parsed.operatingCompany ?? parsed.operating_company ?? "",
        dataYears: Array.isArray(parsed.dataYears) ? parsed.dataYears : [],
        rows: Array.isArray(parsed.rows) ? parsed.rows : []
      };
    } catch (err) {
      console.error("Failed to load step3 from localStorage", err);
      return empty;
    }
  });

  // True exactly when step3.rows came back empty above AND we have a
  // projectID to look the real selection up with -- i.e. we're about to
  // replace that empty default with a real DB fetch a moment later. Every
  // effect below that keys off step3.projectID/step3.rows (the saved
  // sub-elements lookup, and the eager preload that follows it) must wait
  // for this to go false first, exactly like the big comment above this
  // state describes for the original localStorage-only case: acting on
  // the known-incomplete empty default one render early is what caused
  // Quantity to silently reset to 1 before, and would do the same here if
  // these effects ran before the DB fallback resolves.
  const [dbFallbackPending, setDbFallbackPending] = useState(() => {
    try {
      const raw = localStorage.getItem("step3");
      const rows = raw ? JSON.parse(raw)?.rows : null;
      return !(Array.isArray(rows) && rows.length) && !!localStorage.getItem("projectID");
    } catch {
      return !!localStorage.getItem("projectID");
    }
  });

  // Per request: "Print Report" button loading flag -- entirely
  // independent of the step3/dbFallbackPending data-loading state above,
  // just a UI spinner for the new top-right shortcut button (see
  // handlePrintReport near the bottom of this component, right before
  // the JSX return).
  const [printReportLoading, setPrintReportLoading] = useState(false);

  // Same GET /get_project_asset_selection Step3AssetSelectionReadOnly.jsx
  // already calls for its own identical "no draft yet" fallback -- reused
  // here so Step 4 no longer depends on having visited Step 3 first in
  // this session. Builds the exact `rows` shape Step3ReadOnly's own save
  // handler writes to localStorage("step3") (one entry per saved
  // AssetGroup+AssetElement pair, assetElementsData as a 1-item array),
  // so nothing downstream needs to know this came from the API instead of
  // localStorage.
  useEffect(() => {
    if (!dbFallbackPending) return;
    const pid = localStorage.getItem("projectID");
    if (!pid) {
      setDbFallbackPending(false);
      return;
    }

    axios
      .get(`${API_BASE}/get_project_asset_selection?projectId=${encodeURIComponent(pid)}`)
      .then((res) => {
        const d = res.data?.data;
        if (!res.data?.success || !d) return;
        const rows = (d.rows || []).map((r) => ({
          assetGroupID: r.assetGroupID,
          assetGroupName: r.assetGroupName,
          assetElementsData: [
            {
              assetElementID: r.assetElementID,
              assetElementName: r.assetElementName,
              mainElements: r.mainElements || []
            }
          ]
        }));
        setStep3((prev) => ({
          ...prev,
          projectID: pid,
          projectName: prev.projectName || localStorage.getItem("projectName") || "",
          C_ID: prev.C_ID || localStorage.getItem("C_ID") || "",
          operatingCompany: prev.operatingCompany || localStorage.getItem("operatingCompany") || "",
          dataYears: d.dataYear ? [d.dataYear] : prev.dataYears,
          rows
        }));
      })
      .catch((err) => console.error("Failed to load saved asset selection for Step 4 read-only fallback:", err))
      .finally(() => setDbFallbackPending(false));
  }, [dbFallbackPending]);

  const [expandedMain, setExpandedMain] = useState({});
  const [subElements, setSubElements] = useState({});
  const [selectedSubs, setSelectedSubs] = useState({});
  const [loadingSubs, setLoadingSubs] = useState({});
  const [sending, setSending] = useState(false);

  // Add Item modal state — replaces the old prompt()-based addItem().
  // `mainKey` remembers which Main Element's table the modal was opened
  // from, so the item saved via the modal lands back in the right place.
  const [addItemState, setAddItemState] = useState({ show: false });

  // Themed "Cost item inserted successfully" confirmation -- same dark
  // teal modal markup/colors as Step2RiskInflation.jsx's "Budget inserted
  // successfully" popup, replacing the native alert() that used to dump
  // the raw Deleted/Inserted/Skipped counts. savedModalNavigateNext
  // remembers whether OK should also advance to Step 5 (set when the
  // "Next" button triggered the save) or just close (the plain "Save"
  // button, which stays on this page).
  const [showSavedModal, setShowSavedModal] = useState(false);
  const [savedModalNavigateNext, setSavedModalNavigateNext] = useState(false);

  // Standardized alert presentation (matches Step1ProjectInfo.jsx /
  // Step2RiskInflation.jsx / Step3AssetSelection.jsx): validation/server
  // errors render as an inline red "alert-danger" banner right below the
  // wizard tabs instead of a native alert() popup.
  const [serverError, setServerError] = useState(null);

  // Currency symbol for the "Cost of sub elements" figure below -- fetched
  // from the Currency Master table (GET /currency) instead of a hard-coded
  // "SAR"/"$"/"€" literal, so changing that table's Currency_Symbol value
  // changes what's shown here with no code change. Starts blank rather than
  // defaulting to a guessed symbol, so nothing hard-coded ever briefly shows.
  const [currencySymbol, setCurrencySymbol] = useState("");

  useEffect(() => {
    axios
      .get(`${API_BASE}/currency`)
      .then((res) => setCurrencySymbol(res.data?.data?.Currency_Symbol || ""))
      .catch((err) => console.error("Failed to load currency", err));
  }, []);

  // Quick Search for this page, by Short Description -- per request.
  // subElements is already eagerly preloaded for every Main Element as
  // soon as step3.rows is available (see the preload effect above), so
  // this filters purely on data already in state -- no extra fetch. While
  // a search term is active: Asset Element groups / Main Elements with no
  // matching row are hidden entirely, any Main Element that DOES have a
  // match force-expands (overriding expandedMain, which is left untouched
  // so clearing the search restores whatever was manually expanded
  // before), and each visible Main Element's table only lists its
  // matching rows. Selection state (selectedSubs) is never touched by
  // this -- it's a display filter only.
  // Prefill from the header's Quick Search, if that's how the user got
  // here (see Header.jsx's goToSubElement) -- a one-shot localStorage key
  // set right before navigating into this project's wizard at Step 1, so
  // it survives the Step1->2->3->4 click-through and lands the matched
  // term in this page's own search box the moment it's reached. Read once
  // via a lazy initializer (not an effect) so the very first render
  // already has it -- consistent with how step3 above avoids the same
  // "extra render with stale/empty state" class of bug. Removed
  // immediately after reading so it doesn't linger for a later,
  // unrelated visit to this page.
  const [subSearch, setSubSearch] = useState(() => {
    const term = localStorage.getItem("step4QuickSearchTerm");
    if (term) localStorage.removeItem("step4QuickSearchTerm");
    return term || "";
  });
  const subSearchTerm = subSearch.trim().toLowerCase();
  // Per explicit request: the page-level search box must match on Rate as
  // well as Short Description, not Short Description alone -- e.g.
  // typing "150" should surface any sub-element whose Rate is 150.
  const shortDescMatches = (se) => {
    const desc = (se.Short_Description ?? se.Description ?? se.Sub_Element ?? "")
      .toString()
      .toLowerCase();
    if (desc.includes(subSearchTerm)) return true;
    const rate = (se.Rate ?? se.rate ?? "").toString().toLowerCase();
    return rate.includes(subSearchTerm);
  };
  const mainElementHasMatch = (mainKey) => (subElements[mainKey] || []).some(shortDescMatches);

  // Fix, per explicit request: when editing an existing Cost Estimation,
  // Step 4 must be built directly from the project's OWN saved rows in
  // project_sub_elements -- NOT from /get_sub_elements_rate (the Rate
  // Card master list). Rate_Card has no Quantity/Cost of its own, and
  // trying to cross-reference the two by ID was unreliable (ratecard_id
  // here is saved as a formatted string like "RC-1060" while
  // Rate_Card.RC_ID is a bare number, and several rows can share the same
  // main_element/sub_element text). /get_sub_elements_rate is now used
  // ONLY as a fallback the first time a Main Element is opened and it has
  // no saved rows yet (a brand-new estimation) -- see loadSubElements.
  //
  // savedRowsByGroup groups the saved rows by asset_element + main_element
  // (the only identifying text every saved row has), so loadSubElements
  // can pull a Main Element's entire saved row set directly, with no
  // Rate Card lookup or ID matching involved at all.
  // savedRowsLoaded gates the eager-preload effect further down so it
  // doesn't race ahead and fetch from Rate Card before the saved rows are
  // known; a brand-new project (no projectID yet) has nothing saved, so
  // it's marked loaded immediately with an empty map.
  const [savedRowsByGroup, setSavedRowsByGroup] = useState({});
  const [savedRowsLoaded, setSavedRowsLoaded] = useState(false);
  // True once we know this project has SOME saved sub-elements somewhere
  // (in any Main Element) -- i.e. Step 4 has been saved for this project at
  // least once before. Distinguishes two very different reasons a
  // particular Main Element's own savedRows can come back empty:
  //   - the whole project is brand new / Step 4 was never saved -> that
  //     Main Element's catalog should default to "everything selected"
  //     (existing new-estimation behavior), vs.
  //   - the project HAS been saved before, but the user explicitly
  //     unchecked every item under this specific Main Element -> it must
  //     load back as fully unchecked with 0 values, not silently re-select
  //     everything as if nothing had ever been saved.
  const [hasSavedSubElements, setHasSavedSubElements] = useState(false);

  // Fix: this used to be keyed on Asset Element + Main Element name ONLY,
  // with no Asset Group in it at all. Per explicit request/example: when
  // two different Asset Groups (e.g. "Support Facilities" and "Landside")
  // both use Asset Element "General" and a Main Element also named "Site
  // Work" -- a real, common case in this data -- every row saved in
  // project_sub_elements under EITHER group collapsed onto the SAME
  // savedRowsByGroup bucket here. loadSubElements then pulled that one
  // merged bucket (containing BOTH groups' saved rows) for BOTH Asset
  // Groups: whichever of the two catalog items didn't belong to the group
  // currently being viewed still passed the "saved row with no catalog
  // match" fallback further down and got added in as an extra, checked
  // item anyway -- so Support Facilities' own Sub Elements and Landside's
  // own Sub Elements visibly merged into one combined set instead of
  // staying scoped to their own Asset Group, exactly like the earlier
  // mainKeyFor collision (see its comment above) but for the SAVED-rows
  // side of this page instead of the live-session side. Folding the Asset
  // Group name in here keeps each group's saved rows in their own bucket
  // regardless of any Asset Element/Main Element name they happen to
  // share with another group.
  const groupKeyFor = (agName, aeName, mainName) =>
    `${(agName || "").toString().trim().toLowerCase()}||${(aeName || "").toString().trim().toLowerCase()}||${(mainName || "").toString().trim().toLowerCase()}`;

  useEffect(() => {
    // Wait for the step3 DB fallback above (if one is in flight) to land
    // first -- otherwise this would see today's still-empty step3.projectID,
    // conclude "nothing to load" and flip savedRowsLoaded true immediately,
    // then the eager-preload effect further down (gated on savedRowsLoaded)
    // would fire and default every Main Element to the Rate Card catalog
    // before the real saved rows arrive a moment later -- the exact
    // Quantity-resets-to-1 race the comments above already warn about, just
    // via a new source (the DB fallback) instead of the original
    // localStorage-parsing one.
    if (dbFallbackPending) return;
    const pid = step3.projectID;
    if (!pid) {
      setSavedRowsLoaded(true);
      return;
    }
    axios
      .get(`${API_BASE}/get_project_sub_elements?project_id=${encodeURIComponent(pid)}`)
      .then((res) => {
        const rows = Array.isArray(res.data?.data) ? res.data.data : [];
        const grouped = {};
        rows.forEach((r) => {
          const key = groupKeyFor(r.asset_group, r.asset_element, r.main_element);
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push(r);
        });
        setSavedRowsByGroup(grouped);
        setHasSavedSubElements(rows.length > 0);
      })
      .catch((err) => console.error("Failed to load saved sub elements", err))
      .finally(() => setSavedRowsLoaded(true));
  }, [dbFallbackPending, step3.projectID]);

  // (step3 is populated synchronously at useState-init time above when a
  // localStorage draft exists -- see the long comment there for why the
  // old async version of this same parsing, as a useEffect, was the
  // actual root cause of the Quantity bug -- or via the dbFallbackPending
  // effect above when there's no draft, gated the same careful way.)

  // Fix: ME_ID alone is not reliable as a unique key here -- the same
  // pattern already found and fixed for Step 3's checkbox toggle bug
  // ("the Main Elements master list can have IDs that aren't guaranteed
  // unique... shared/reused across different Asset Group/Element pairs")
  // turns out to also affect Rate_Card's own ME_ID column: two
  // differently-NAMED Main Elements (e.g. "General" and "Sitework" under
  // the same Asset Group/Element) can share one ME_ID value. Since every
  // Main Element's own data (subElements/selectedSubs/expandedMain, and
  // the get_sub_elements_rate fetch below) is keyed purely off this
  // string, an ME_ID collision made two distinct Main Elements collapse
  // onto the same key -- both then showed the exact same (combined) sub-
  // element list and cost, and the Asset Element's total double-counted
  // it. Including the Main Element's own name in the key keeps them
  // apart even when their ME_ID collides.
  //
  // Fix: the same collision pattern turned out to exist ONE LEVEL UP
  // too. "General" as an Asset Element is a single shared master row
  // reused across several different Asset Groups (Landside, Airside,
  // Terminals, Support Facilities, etc all picking the same "General"
  // Asset Element in Step 3) -- so aeID alone is NOT unique per Asset
  // Group either. Without rowIndex here, "Landside > General >
  // Sitework" and "Airside > General > Sitework" produced the EXACT
  // SAME mainKey, so they shared one subElements/selectedSubs bucket:
  // whichever group loaded first populated it, the other three groups
  // just displayed that same cached data (hence identical totals across
  // all four), and buildItemsPayload's per-mainKey loop only ever
  // walked that data once -- which is why 26 checked boxes across
  // several such groups only ever produced 14 saved rows: everything
  // past the first group sharing a mainKey was invisible to the save,
  // not merely deduped. rowIndex (step3.rows' own array index -- each
  // entry there is one Asset Group selection instance) is guaranteed
  // unique per Asset Group regardless of any shared master IDs
  // underneath it, so folding it in here keeps every Asset Group's
  // state independent no matter what Asset Element/Main Element ids it
  // happens to share with another group.
  const mainKeyFor = (rowIndex, aeID, me) =>
    `${rowIndex}_${aeID}_${me.ME_ID}_${(me.Main_Element || "").toString().trim().toLowerCase()}`;

  // `autoExpand` (default true, matching every existing manual-click call
  // site below) opens the Main Element's accordion section as a side
  // effect of loading. The eager preload effect further down passes
  // autoExpand:false -- it needs the same fetch-and-select-all so the
  // running Total Amount is correct the moment the page loads (see that
  // effect's comment), but the sections themselves should stay collapsed
  // exactly as before, not all pop open unasked.
  const loadSubElements = useCallback(
    async (rowIndex, aeID, mainElement, opts = {}) => {
      const { autoExpand = true } = opts;
      const row = (step3.rows || [])[rowIndex];
      if (!row) return;
      if (!aeID || !mainElement || !mainElement.ME_ID) return;

      const mainKey = mainKeyFor(rowIndex, aeID, mainElement);

      // Already loaded -- per request, expanding/collapsing an already-
      // loaded Main Element must only toggle visibility, never touch the
      // checkbox selection. This used to re-select EVERY row here on
      // every re-expand (select-all), silently discarding whatever the
      // user had actually checked/unchecked the last time this section
      // was open. Selection is now only ever initialized once, at the
      // point each of the two fetch branches below first populates
      // subElements[mainKey] for this mainKey.
      if (Array.isArray(subElements[mainKey]) && subElements[mainKey].length > 0) {
        if (autoExpand) {
          setExpandedMain((p) => ({ ...p, [mainKey]: true }));
        }
        return;
      }

      setLoadingSubs((p) => ({ ...p, [mainKey]: true }));
      if (autoExpand) {
        setExpandedMain((p) => ({ ...p, [mainKey]: true }));
      }

      const aeName = (row.assetElementsData || []).find((a) => a.assetElementID === aeID)?.assetElementName;
      const agName = row.assetGroupName ?? row.asset_group ?? null;
      const groupKey = groupKeyFor(agName, aeName, mainElement.Main_Element);
      const savedRows = savedRowsByGroup[groupKey] || [];

      const c_id = step3.C_ID ?? "";
      const ag_id = row.assetGroupID ?? row.asset_group_id ?? "";
      const ae_id = aeID ?? "";
      const me_id = mainElement.ME_ID ?? "";
      // Sent alongside me_id, not instead of it -- see the mainKeyFor
      // comment above: Rate_Card's ME_ID can be shared by two differently-
      // named Main Elements, so the backend now also matches on this name
      // to keep results scoped to the one Main Element actually being
      // opened, not everything under that (colliding) ME_ID.
      const main_element_name = mainElement.Main_Element ?? "";

      const url = `${API_BASE}/get_sub_elements_rate?c_id=${encodeURIComponent(
        c_id
      )}&ag_id=${encodeURIComponent(ag_id)}&ae_id=${encodeURIComponent(ae_id)}&me_id=${encodeURIComponent(
        me_id
      )}&main_element=${encodeURIComponent(main_element_name)}`;

      // Text-based match key (Sub Element + Short Description + Rate) used
      // below to line up a Rate Card catalog row with a saved
      // project_sub_elements row -- NOT ID-based, because ratecard_id is
      // saved as a formatted string like "RC-1060" while Rate_Card.RC_ID
      // is a bare number, so ID cross-referencing is unreliable (several
      // rows can also share the same sub_element text alone, which is why
      // Rate/Short Description are folded into the key too).
      const matchKey = (subText, shortDesc, rate) =>
        `${(subText ?? "").toString().trim().toLowerCase()}||${(shortDesc ?? "").toString().trim().toLowerCase()}||${Number(rate) || 0}`;

      const dedupeCatalog = (dataArray) => {
        const map = new Map();
        dataArray.forEach((se) => {
          const subText = (se.Sub_Element ?? se.Short_Description ?? "").toString().trim().toLowerCase();
          const unitText = (se.Unit ?? "").toString().trim().toLowerCase();
          const rateText = String(se.Rate ?? se.rate ?? "").toString().trim();
          const rcid = String(se.RC_ID ?? se.SE_ID ?? "").toString().trim();
          const key = `${subText}||${unitText}||${rateText}||${rcid}`;
          if (!map.has(key)) map.set(key, se);
        });
        return Array.from(map.values());
      };

      try {
        const res = await axios.get(url, { timeout: 20000 });
        const catalog = dedupeCatalog(Array.isArray(res.data?.data) ? res.data.data : []);

        if (savedRows.length > 0) {
          // Per explicit request ("Preserve Selected Items When
          // Navigating Back"): show the FULL Rate Card catalog for this
          // Main Element, not just whatever was previously checked --
          // only checked items are ever written to project_sub_elements
          // (buildItemsPayload below filters to `selected` before
          // saving), so that table alone can't distinguish "this item
          // exists but was left unchecked" from "this item isn't part of
          // the catalog at all". Reconstructing from project_sub_elements
          // ALONE (the previous approach) silently dropped every
          // unchecked item from view instead of showing it, unchecked, as
          // it should -- so Back didn't actually preserve the full
          // checkbox state, just a checked-only subset of it.
          const savedByKey = new Map();
          savedRows.forEach((r) => {
            savedByKey.set(matchKey(r.sub_element, r.description, r.rate), r);
          });

          const merged = catalog.map((se, idx) => {
            const key = matchKey(se.Sub_Element, se.Short_Description ?? se.Description, se.Rate ?? se.rate);
            const saved = savedByKey.get(key);
            const seID = se.SE_ID ?? se.RC_ID ?? `SE_${Date.now()}_${idx}`;
            if (saved) {
              savedByKey.delete(key);
              return {
                SE_ID: seID,
                RC_ID: se.RC_ID ?? se.SE_ID ?? null,
                Sub_Element: se.Sub_Element ?? saved.sub_element ?? "",
                Short_Description: se.Short_Description ?? saved.description ?? "",
                Unit: se.Unit ?? saved.unit ?? "",
                Rate: Number(saved.rate ?? se.Rate ?? se.rate ?? 0),
                Quantity: Number(saved.quantity ?? 1),
                Cost: Number(saved.cost ?? 0),
                asset_class: se.asset_class ?? saved.asset_class ?? null,
                __checked: true
              };
            }
            return {
              SE_ID: seID,
              RC_ID: se.RC_ID ?? se.SE_ID ?? null,
              Sub_Element: se.Sub_Element ?? "",
              Short_Description: se.Short_Description ?? se.Description ?? "",
              Unit: se.Unit ?? "",
              Rate: Number(se.Rate ?? se.rate ?? 0),
              // Unchecked items must show Quantity/Cost = 0, not the Rate
              // Card's default Quantity of 1 -- per explicit request: "When
              // a Main Element is unchecked ... Their values must remain
              // 0." This applies to any catalog item that wasn't part of
              // this project's saved (checked) selection.
              Quantity: 0,
              Cost: 0,
              asset_class: se.asset_class ?? se.Class ?? se.Category ?? null,
              __checked: false
            };
          });

          // Saved rows that didn't match any catalog item -- a custom item
          // added via "Add Item" (lives only in project_sub_elements, no
          // Rate_Card counterpart) -- still need to show up, checked, with
          // their saved data intact.
          let extraIdx = 0;
          savedByKey.forEach((r) => {
            merged.push({
              SE_ID: r.ratecard_id ?? `SAVED_EXTRA_${extraIdx++}_${Date.now()}`,
              RC_ID: r.ratecard_id ?? null,
              Sub_Element: r.sub_element ?? "",
              Short_Description: r.description ?? r.sub_element ?? "",
              Unit: r.unit ?? "",
              Rate: Number(r.rate ?? 0),
              Quantity: Number(r.quantity ?? 0),
              Cost: Number(r.cost ?? 0),
              asset_class: r.asset_class ?? null,
              __checked: true
            });
          });

          setSubElements((prev) => ({ ...prev, [mainKey]: merged }));
          setSelectedSubs((prev) => ({
            ...prev,
            [mainKey]: merged.filter((m) => m.__checked).map((m) => m.SE_ID)
          }));
          return;
        }

        // No saved rows for THIS Main Element. Two very different cases:
        if (hasSavedSubElements) {
          // The project has been saved before (it has sub-elements saved
          // under other Main Elements) -- an empty group here means the
          // user explicitly unchecked every item under this Main Element
          // and saved that. Per explicit request ("When a Main Element is
          // unchecked ... All Sub Elements ... must remain unchecked ...
          // Their values must remain 0" and "the application must retrieve
          // the saved data from the database table, not reload the
          // original/master selection data"): show the full catalog so the
          // items are still visible, but fully UNCHECKED with 0 values --
          // never fall back to "everything selected" here, or Back would
          // silently re-select a Main Element the user had deliberately
          // cleared.
          const unchecked = catalog.map((se, idx) => ({
            SE_ID: se.SE_ID ?? se.RC_ID ?? `SE_${Date.now()}_${idx}`,
            RC_ID: se.RC_ID ?? se.SE_ID ?? null,
            Sub_Element: se.Sub_Element ?? "",
            Short_Description: se.Short_Description ?? se.Description ?? se.Sub_Element ?? "",
            Unit: se.Unit ?? "",
            Rate: Number(se.Rate ?? se.rate ?? 0),
            Quantity: 0,
            Cost: 0,
            asset_class: se.asset_class ?? se.Class ?? se.Category ?? null,
            __checked: false
          }));

          setSubElements((prev) => ({ ...prev, [mainKey]: unchecked }));
          setSelectedSubs((prev) => ({ ...prev, [mainKey]: [] }));
          return;
        }

        // Truly brand-new estimation -- Step 4 has never been saved for
        // this project at all -- fall back to the Rate Card master list,
        // same as before, with the Rate Card's own default Quantity of 1,
        // everything selected by default.
        const deduped = catalog.map((se, idx) => ({
          ...se,
          SE_ID: se.SE_ID ?? se.RC_ID ?? `SE_${Date.now()}_${idx}`,
          RC_ID: se.RC_ID ?? se.SE_ID ?? null,
          Quantity: se.Quantity ?? 1,
          Rate: Number(se.Rate ?? se.rate ?? 0),
          Cost: Number(se.Rate ?? se.rate ?? 0) * Number(se.Quantity ?? 1),
          Short_Description: se.Short_Description ?? se.Description ?? se.Sub_Element ?? "",
          asset_class: se.asset_class ?? se.Class ?? se.Category ?? null
        }));

        setSubElements((prev) => ({ ...prev, [mainKey]: deduped }));
        setSelectedSubs((prev) => ({ ...prev, [mainKey]: deduped.map((s) => s.SE_ID) }));
      } catch (err) {
        console.error("Error loading sub elements for", mainKey, err);
        if (savedRows.length > 0) {
          // Catalog fetch failed but the project's saved rows are still
          // known -- fall back to showing just those (checked) rather
          // than nothing, so a transient network error doesn't wipe a
          // previously-saved selection out of view.
          const fromSaved = savedRows.map((r, idx) => ({
            SE_ID: r.ratecard_id ?? `SAVED_${idx}_${Date.now()}`,
            RC_ID: r.ratecard_id ?? null,
            Sub_Element: r.sub_element ?? "",
            Short_Description: r.description ?? r.sub_element ?? "",
            Unit: r.unit ?? "",
            Rate: Number(r.rate ?? 0),
            Quantity: Number(r.quantity ?? 0),
            Cost: Number(r.cost ?? 0),
            asset_class: r.asset_class ?? null
          }));
          setSubElements((prev) => ({ ...prev, [mainKey]: fromSaved }));
          setSelectedSubs((prev) => ({ ...prev, [mainKey]: fromSaved.map((s) => s.SE_ID) }));
        } else {
          setSubElements((prev) => ({ ...prev, [mainKey]: [] }));
        }
      } finally {
        setLoadingSubs((p) => ({ ...p, [mainKey]: false }));
      }
    },
    [step3.rows, subElements, step3.C_ID, savedRowsByGroup, hasSavedSubElements]
  );

  // Previously, the running "Total amount" stayed 0.00 until the user
  // clicked the + on every Main Element -- that's what actually
  // triggered loadSubElements (see toggleExpand below), so nothing had
  // a Cost to sum until then. This preloads every Main Element's sub
  // elements as soon as the project/asset data (step3.rows) is
  // available, with autoExpand:false so the accordion sections stay
  // collapsed exactly as before -- only the data backing the total
  // loads eagerly, not the UI. Each call is independent and only ever
  // fetches once (loadSubElements' own already-loaded check makes the
  // rest of these a no-op), so this doesn't refetch on every render.
  useEffect(() => {
    // Wait for the saved-rows lookup (above) to finish first, so this
    // doesn't preload every Main Element from the Rate Card before it's
    // known which ones actually have saved project_sub_elements rows.
    if (!savedRowsLoaded) return;
    const rows = step3.rows || [];
    rows.forEach((row, rowIndex) => {
      (row.assetElementsData || []).forEach((ae) => {
        (ae.mainElements || []).forEach((main) => {
          loadSubElements(rowIndex, ae.assetElementID, main, { autoExpand: false });
        });
      });
    });
    // savedRowsByGroup is deliberately included (on top of the original
    // step3.rows/savedRowsLoaded pair) as a defensive measure: this effect
    // must never fire using a stale/empty savedRowsByGroup while the real
    // one is still in flight (that exact race -- savedRowsLoaded flipping
    // true a render before savedRowsByGroup itself had the fetched data --
    // was the root cause of Quantity always showing 1 on edit). Now fixed
    // at the source (step3 loads synchronously, so savedRowsLoaded can't
    // go true prematurely), but keeping this dependency here means if that
    // ever changes again, this still re-runs once the real data lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step3.rows, savedRowsLoaded, savedRowsByGroup]);

  // Per request: the +/- expand/collapse control only shows or hides a
  // Main Element's table -- it must never change which checkboxes are
  // checked. loadSubElements already handles selection correctly on its
  // own: it initializes selectedSubs exactly once, the first time a
  // mainKey's data is actually fetched (see its two fetch branches
  // below), and leaves it alone on every call after that (see the
  // "already loaded" branch above). This used to ALSO force-select every
  // row here, after every single expand -- on top of being redundant
  // with loadSubElements' own initial selection, it's what caused a
  // manual collapse+re-expand to silently reset the user's unchecked
  // boxes back to fully checked.
  const toggleExpand = async (rowIndex, aeID, mainElement) => {
    const mainKey = mainKeyFor(rowIndex, aeID, mainElement);
    const currentlyExpanded = !!expandedMain[mainKey];

    if (currentlyExpanded) {
      setExpandedMain((p) => ({ ...p, [mainKey]: false }));
      return;
    }

    setExpandedMain((p) => ({ ...p, [mainKey]: true }));
    await loadSubElements(rowIndex, aeID, mainElement);
  };

  // Unchecking an item must zero its Quantity/Cost (not just drop it from
  // selectedSubs) -- per explicit request, unchecked sub-elements' values
  // must read 0, both live and after a Next->Back round-trip (the
  // round-trip side is handled by loadSubElements' merge logic above).
  // Re-checking restores a sensible default Quantity of 1 so the item
  // isn't left showing 0/0 while still checked.
  const toggleSelectSub = (mainKey, seID) => {
    setSelectedSubs((prev) => {
      const current = prev[mainKey] || [];
      if (current.includes(seID)) {
        return { ...prev, [mainKey]: current.filter((id) => id !== seID) };
      } else {
        return { ...prev, [mainKey]: [...current, seID] };
      }
    });
    setSubElements((prev) => {
      const isCurrentlySelected = (selectedSubs[mainKey] || []).includes(seID);
      const updated = (prev[mainKey] || []).map((s) => {
        if (s.SE_ID !== seID) return s;
        const rate = Number(s.Rate ?? 0);
        if (isCurrentlySelected) {
          // Was checked -> being unchecked now: zero it out.
          return { ...s, Quantity: 0, Cost: 0 };
        }
        // Was unchecked -> being checked now: restore a default quantity.
        const qty = Number(s.Quantity) > 0 ? Number(s.Quantity) : 1;
        return { ...s, Quantity: qty, Cost: rate * qty };
      });
      return { ...prev, [mainKey]: updated };
    });
  };

  const selectAll = (mainKey) => {
    const allIDs = (subElements[mainKey] || []).map((s) => s.SE_ID);
    setSelectedSubs((prev) => ({ ...prev, [mainKey]: allIDs }));
    // Restore a default quantity for any item left at 0 from a prior
    // uncheck, so "Select All" doesn't leave checked rows showing 0/0.
    setSubElements((prev) => {
      const updated = (prev[mainKey] || []).map((s) => {
        if (Number(s.Quantity) > 0) return s;
        const rate = Number(s.Rate ?? 0);
        return { ...s, Quantity: 1, Cost: rate };
      });
      return { ...prev, [mainKey]: updated };
    });
  };

  const uncheckAll = (mainKey) => {
    setSelectedSubs((prev) => ({ ...prev, [mainKey]: [] }));
    // Per explicit request: unchecking every Sub Element under a Main
    // Element must zero all of their Quantity/Cost values, not just clear
    // the checkmarks.
    setSubElements((prev) => {
      const updated = (prev[mainKey] || []).map((s) => ({ ...s, Quantity: 0, Cost: 0 }));
      return { ...prev, [mainKey]: updated };
    });
  };

  const deleteSelected = (mainKey) => {
    const selected = selectedSubs[mainKey] || [];
    if (selected.length === 0) return;
    setSubElements((prev) => {
      const remaining = (prev[mainKey] || []).filter((s) => !selected.includes(s.SE_ID));
      return { ...prev, [mainKey]: remaining };
    });
    setSelectedSubs((prev) => ({ ...prev, [mainKey]: [] }));
  };

  // Opens the Add Item modal (RATE CARD form) for the Main Element the
  // "Add Item" link was clicked in. Class/Asset Group/Asset Element/Main
  // Element are pre-filled from the current context; the modal lets the
  // user override any of them if the new catalog item actually belongs
  // somewhere else.
  const openAddItemModal = (mainKey, row, ae, main) => {
    setAddItemState({
      show: true,
      mainKey,
      agId: row.assetGroupID ?? row.asset_group_id ?? "",
      agName: row.assetGroupName ?? row.asset_group ?? "",
      aeId: ae.assetElementID ?? "",
      aeName: ae.assetElementName ?? "",
      meId: main.ME_ID ?? "",
      meName: main.Main_Element ?? ""
    });
  };

  const closeAddItemModal = () => setAddItemState({ show: false });

  // Called once the modal has successfully saved the new row into
  // Rate_Card (via /insert_rate_card, which hands back the new row's
  // RC_ID -- see newSE.RC_ID) — appends it to the current Main Element's
  // sub-element table and selects it, same end result the old
  // prompt()-based addItem() produced, but backed by the real database
  // now.
  //
  // Per explicit request ("Step 4 -- Add Item" flow), this can't stop at
  // just updating local React state and waiting for the user to later
  // click Save/Next: the new project_sub_elements row (referencing the
  // Rate_Card row just created via its RC_ID, with the Quantity entered
  // in the modal) must be persisted right away. setSubElements/
  // setSelectedSubs above are async, so building the payload from
  // `subElements`/`selectedSubs` directly here would still see the
  // pre-add snapshot -- merged{Sub,Selected} below compute what the state
  // will become, and are passed straight into saveToBackend's overrides
  // so this save reflects the new item immediately, in this same tick.
  const handleRateCardItemAdded = (newSE) => {
    const mainKey = addItemState.mainKey;
    if (!mainKey) return;

    const mergedSubElements = { ...subElements, [mainKey]: [...(subElements[mainKey] || []), newSE] };
    const mergedSelectedSubs = { ...selectedSubs, [mainKey]: [...(selectedSubs[mainKey] || []), newSE.SE_ID] };

    setSubElements(mergedSubElements);
    setSelectedSubs(mergedSelectedSubs);
    setAddItemState({ show: false });

    // Only auto-persist to project_sub_elements once this is a real,
    // already-created project (it needs a project_id) -- a brand-new
    // estimation that hasn't reached that point yet just keeps the new
    // item in local state, same as every other selection here, to be
    // saved for the first time when the user clicks Save/Next.
    if (step3.projectID) {
      saveToBackend(false, {
        subElementsOverride: mergedSubElements,
        selectedSubsOverride: mergedSelectedSubs,
        silent: true
      });
    }
  };

  const updateQuantity = (mainKey, seID, qtyRaw) => {
    const qty = Number(qtyRaw) || 0;
    setSubElements((prev) => {
      const updated = (prev[mainKey] || []).map((s) => {
        if (s.SE_ID === seID) {
          const rate = Number(s.Rate ?? 0);
          return { ...s, Quantity: qty, Cost: rate * qty };
        }
        return s;
      });
      return { ...prev, [mainKey]: updated };
    });
  };

  // `overrideSubElements`/`overrideSelectedSubs` let a caller build the
  // payload from a state snapshot that hasn't actually landed in
  // `subElements`/`selectedSubs` yet -- see handleRateCardItemAdded below,
  // which needs to persist a just-added item immediately, in the same
  // tick it was added, rather than wait a render for setState to flush
  // and risk reading the stale (pre-add) closure values here.
  const buildItemsPayload = useCallback((overrideSubElements, overrideSelectedSubs) => {
    const subElementsSrc = overrideSubElements || subElements;
    const selectedSubsSrc = overrideSelectedSubs || selectedSubs;
    const rows = Array.isArray(step3.rows) ? step3.rows : [];
    const items = [];
    const seen = new Set();

    rows.forEach((row, rowIndex) => {
      const assetGroupName = row.assetGroupName ?? row.asset_group ?? null;
      const assetElements = Array.isArray(row.assetElementsData) ? row.assetElementsData : [];

      assetElements.forEach((ae) => {
        const aeName = ae.assetElementName ?? ae.asset_element ?? null;
        const aeID = ae.assetElementID ?? null;
        const mainElements = Array.isArray(ae.mainElements) ? ae.mainElements : [];

        mainElements.forEach((me) => {
          const mainName = me.Main_Element ?? me.main_element ?? null;
          const mainKey = mainKeyFor(rowIndex, aeID, me);

          const subs = Array.isArray(subElementsSrc[mainKey]) ? subElementsSrc[mainKey] : [];
          const selected = selectedSubsSrc[mainKey] || [];

          subs.forEach((sub) => {
            if (!selected.includes(sub.SE_ID)) return;

            // Fix: this key used to be Sub_Element+Unit+Rate+RC_ID alone,
            // with no Asset Group/Element/Main Element in it at all --
            // so the SAME underlying Rate_Card row selected under
            // several DIFFERENT Main Elements (a real, common case in
            // this data: the same "General"/"Sitework" Sub Element +
            // Rate combination legitimately appears, and gets checked,
            // under Landside > General, Airside > General, Terminals >
            // General AND Support Facilities > General -- each its own
            // selection, meant to become its own project_sub_elements
            // row) collapsed onto ONE `seen` entry, silently dropping
            // every occurrence after the first. That's why checking 26
            // boxes only ever produced 14 saved rows: every group past
            // the first with a repeated Sub Element/Rate got eaten by
            // this dedupe. `mainKey` already uniquely identifies "this
            // exact Asset Element + Main Element" (see mainKeyFor above),
            // so folding it into the key keeps the SAME item selected in
            // different groups from ever being treated as a duplicate of
            // itself -- this only needs to catch genuine duplicates
            // *within* one Main Element's own list.
            const subKey = (
              mainKey +
              "|" +
              (sub.Sub_Element ?? sub.sub_element ?? sub.Short_Description ?? "") +
              "|" +
              (sub.Unit ?? "") +
              "|" +
              String(sub.Rate ?? sub.rate ?? "") +
              "|" +
              String(sub.RC_ID ?? sub.SE_ID ?? "")
            )
              .toString()
              .trim()
              .toLowerCase();

            if (seen.has(subKey)) return;
            seen.add(subKey);

            items.push({
              asset_group: assetGroupName,
              asset_element: aeName,
              main_element: mainName,
              sub_element: sub.Sub_Element ?? sub.sub_element ?? sub.Short_Description ?? null,
              description: sub.Description ?? sub.Short_Description ?? null,
              unit: sub.Unit ?? sub.unit ?? null,
              rate: Number(sub.Rate ?? sub.rate ?? 0),
              quantity: Number(sub.Quantity ?? sub.quantity ?? 0),
              cost: Number(sub.Cost ?? sub.cost ?? 0),
              ratecard_id: sub.RC_ID ?? sub.SE_ID ?? null,
              asset_class: sub.asset_class ?? null,
              operating_company: step3.operatingCompany ?? step3.operating_company ?? null
            });
          });
        });
      });
    });

    console.log("Built items payload count (selected only):", items.length);
    return items;
  }, [step3, subElements, selectedSubs]);

  const getTotalAmount = useCallback(() => {
    let total = 0;
    Object.keys(selectedSubs).forEach((mainKey) => {
      const sel = selectedSubs[mainKey] || [];
      (subElements[mainKey] || []).forEach((se) => {
        if (sel.includes(se.SE_ID)) {
          total += Number(
            se.Cost ?? (Number(se.Rate ?? 0) * Number(se.Quantity ?? 0))
          ) || 0;
        }
      });
    });
    return total;
  }, [selectedSubs, subElements]);

  // `opts.subElementsOverride`/`opts.selectedSubsOverride` pass straight
  // through to buildItemsPayload -- see its comment above. `opts.silent`
  // skips the "Saved" confirmation modal, for the auto-save that runs
  // right after Add Item (see handleRateCardItemAdded below): that's a
  // background persist the user didn't explicitly ask for by clicking
  // Save/Next, so popping the same confirmation dialog for it would read
  // as if Save/Next had been clicked.
  const saveToBackend = useCallback(async (navigateNext = false, opts = {}) => {
    const { subElementsOverride, selectedSubsOverride, silent = false } = opts;
    if (sending) return false;
    setServerError(null);
    setSending(true);
    try {
      const items = buildItemsPayload(subElementsOverride, selectedSubsOverride);
      if (!Array.isArray(items) || items.length === 0) {
        setServerError("No items selected to save.");
        setSending(false);
        return false;
      }

      const payload = {
        project_id: step3.projectID || null,
        project_name: step3.projectName || null,
        operating_company: step3.operatingCompany ?? step3.operating_company ?? null,
        items
      };

      const res = await axios.post(`${API_BASE}/insert_sub_elements`, payload, {
        headers: { "Content-Type": "application/json" },
        timeout: 120000
      });

      const data = res?.data;
      if (!data || data.success !== true) {
        setServerError(data?.message || data?.error || "Save failed. See console for details.");
        setSending(false);
        return false;
      }

      // /insert_sub_elements reports success:true even when some
      // individual rows failed to insert (it deliberately keeps going
      // instead of aborting the whole save over one bad row -- see its
      // per-item try/except) -- `inserted` can come back lower than the
      // number of checked items sent. That gap was invisible before:
      // this always showed the same "Saved" confirmation regardless, so
      // there was no way to tell "26 checked" apart from "14 actually
      // landed in the database" without querying the table directly.
      // Surface it here instead, with the actual per-row reasons the
      // server recorded, so a shortfall is visible immediately.
      const sentCount = items.length;
      const insertedCount = Number(data.inserted ?? sentCount);
      if (insertedCount < sentCount) {
        const reasons = Array.isArray(data.errors) ? data.errors.slice(0, 5) : [];
        const reasonText = reasons
          .map((e) => `#${e.index}: ${e.reason}${e.item?.sub_element ? ` (${e.item.sub_element})` : ""}`)
          .join("; ");
        setServerError(
          `Only ${insertedCount} of ${sentCount} checked items were actually saved` +
          (data.skipped ? ` (${data.skipped} skipped)` : "") +
          (reasonText ? `. First reasons: ${reasonText}` : ". Check server logs for the full list.")
        );
        setSending(false);
        return false;
      }

      setSending(false);
      if (silent) return true;

      // Show the themed confirmation modal instead of a native alert();
      // navigation (if any) happens once the user dismisses it via OK --
      // see handleSavedModalOk below.
      setSavedModalNavigateNext(navigateNext);
      setShowSavedModal(true);
      return true;
    } catch (err) {
      console.error("Save error:", err);
      setServerError(err.response?.data?.message || err.response?.data?.error || "Save error. Please try again.");
      setSending(false);
      return false;
    }
  }, [sending, buildItemsPayload, step3]);

  const handleSavedModalOk = () => {
    setShowSavedModal(false);
    if (savedModalNavigateNext) {
      navigate(ROUTES.COST_ESTIMATION_STEP5);
    }
  };

  const totalAmount = getTotalAmount();

  // persist total for next step
  localStorage.setItem("step5TotalCost", String(totalAmount));
  localStorage.setItem("step5_snapshot", JSON.stringify({ subElements, selectedSubs }));

  // Per request: once a project has been through the full wizard and
  // Finish has been clicked, every read-only step page for it gets a
  // "Print Report" / "View Dashboard" shortcut in the top-right corner,
  // below the breadcrumb -- no need to jump back to the Estimation
  // Summary page just to print the report or check the portfolio
  // Dashboard. Print Report reuses printProjectReport.js's self-contained
  // generator (the same one ProjectList.jsx's "Estimated" column icon
  // already calls) -- given just a projectId, it fetches everything
  // itself and opens the report exactly the same way. Unlike some of the
  // other read-only steps, this page's own API_BASE is already the plain
  // origin (no "/api" prefix to route around).
  const handlePrintReport = async () => {
    const projectId = localStorage.getItem("projectID");
    if (!projectId) return;
    setPrintReportLoading(true);
    try {
      await printProjectReport({ authFetch, API_BASE, projectId });
    } catch (err) {
      console.error("Failed to generate report", err);
    } finally {
      setPrintReportLoading(false);
    }
  };

  return (
    <>
      {/* Was a plain "Start Cost Estimation" title -- replaced with the
          actual project name (already loaded from localStorage("step3")
          above) in a white block, so this step reads as "which project
          am I editing" instead of a generic wizard label repeated on
          every step. */}
      <div className="page-title-head d-flex align-items-center flex-wrap gap-2">
        <div className="flex-grow-1">
          {/* Per request, this is now a breadcrumb trail instead of a
              plain bordered/uppercase title: "Cost Estimation / Element /
              Project : {name}", each non-current segment (and the "/"
              separators) in #788ca5 at 14px, normal case, no underline
              -- matching the reference design. The current segment
              ("Project : {name}") stays semibold in the app's brand
              teal so it still reads as the active/current item, same
              idea as the reference's blue "Portfolio" segment. */}
          <nav aria-label="breadcrumb" className="d-inline-block">
            <span style={{ fontSize: 16, color: "#02bc9c" }}>Cost Estimation</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }} className="mx-2">/</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }}>Project</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }} className="mx-2">/</span>
            <span className="fw-semibold" style={{ fontSize: 16, color: "#02bc9c" }}>
              {step3.projectName || "—"}
            </span>
          </nav>
        </div>

        {/* Per request: "Print Report" / "View Dashboard" shortcut, top
            right corner below the header, same on every read-only wizard
            step page for a finished project. */}
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <button
            type="button"
            className="btn btn-sm text-white px-3 py-2 fw-semibold d-inline-flex align-items-center gap-2"
            style={{ backgroundColor: "#124d52", borderRadius: 2 }}
            onClick={handlePrintReport}
            disabled={printReportLoading}
          >
            <i className="ti ti-printer fs-5" />
            {printReportLoading ? "Preparing..." : "Print Report"}
          </button>
          <button
            type="button"
            className="btn btn-sm px-3 py-2 fw-semibold d-inline-flex align-items-center gap-2"
            style={{ backgroundColor: "#02bc9c", color: "#fff", borderRadius: 2 }}
            onClick={() => navigate(ROUTES.DASHBOARD)}
          >
            <i className="ti ti-dashboard fs-5" />
            View Dashboard
          </button>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              {/* Wizard progress bar -- see the note above WIZARD_STEPS in
                  Step1ProjectInfo.jsx for why this is a fixed percentage
                  per page instead of the theme's JS-driven one. */}
              <div className="progress mb-4" style={{ height: 6 }}>
                <div className="progress-bar wizard-progress-bar" style={{ width: "100%" }} />
              </div>

              <ul className="nav nav-tabs wizard-tabs w-100 row g-2 border-0" role="tablist">
                {WIZARD_STEPS.map((step, idx) => (
                  <li className="nav-item col-12 col-lg" key={step.key}>
                    <span
                      className="nav-link h-100 wizard-item-done"
                      role="button"
                      tabIndex={0}
                      style={{ cursor: "pointer" }}
                      onClick={() => navigate(STEP_VIEW_ROUTES[step.key])}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          navigate(STEP_VIEW_ROUTES[step.key]);
                        }
                      }}
                    >
                      <span className="d-flex align-items-center">
                        {step.key === "budget" ? (
                          <svg
                            className="flex-shrink-0"
                            style={{ width: "32px", height: "32px" }}
                            viewBox="0 0 917 1025"
                            fill="currentColor"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path fillRule="evenodd" d="M 916 834 L 570 908 L 550 964 L 542 1001 L 540 1024 L 884 951 L 893 932 L 904 899 L 912 865 Z M 646 54 L 539 144 L 539 473 L 435 496 L 430 1 L 324 89 L 324 518 L 83 570 L 51 682 L 323 629 L 324 767 L 32 830 L 0 946 L 310 881 L 346 863 L 428 746 L 437 604 L 539 584 L 539 803 L 885 730 L 916 614 L 646 670 L 646 561 L 885 509 L 916 397 L 646 449 Z" />
                          </svg>
                        ) : (
                          <i className={`ti ${step.icon} fs-32 flex-shrink-0`} />
                        )}
                        <span className="flex-grow-1 ms-2 text-truncate">
                          <span className="mb-0 lh-base d-block fw-semibold text-body fs-base">{step.label}</span>
                          <span className="fs-xxs mb-0">{step.sub}</span>
                        </span>
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
              <hr className="mt-3" />

              {serverError && (
                <div className="alert alert-danger" role="alert">
                  {serverError}
                </div>
              )}

              {/* Section header. The "Project: ... Data Years: ..." line
                  that used to sit under "Sub Element" was dropped -- the
                  page-level header above the wizard tabs already shows
                  "Project : <name>", so this was a plain duplicate. The
                  Save button that used to live here moved down next to
                  Back/Next -- see the bottom action row below. Per a later
                  request, each Asset Group/Asset Element section used to
                  show its own "Cost of main elements = ..." total, which
                  read as several different totals down the page. Per the
                  latest request, that's replaced with exactly one combined
                  total for the whole page (sum across every Asset Group),
                  shown below using the existing totalAmount / getTotalAmount()
                  page-wide sum. */}
              {/* This gap was previously tightened to mb-0 (see history),
                  but per a later request that read as too cramped once the
                  search box sits directly above the "Select All / Uncheck
                  All / Remove" button row for the first Asset Group/Asset
                  Element section -- mb-3 restores a visible gap between
                  them. */}
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                <div>
                  {/* Per request, the separate right-aligned "Total Sum"
                      line (see below) is folded into this same heading
                      instead -- "Main Element (Total Sum = ...)" as one
                      line, rather than two separate texts on opposite
                      sides of the header row. */}
                  <h5 className="text-green mb-0 fs-4 fw-bold">
                    Main Element (Total Sum ={" "}
                    {currencySymbol}{" "}
                    {totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                  </h5>
                </div>
                <div className="app-search" style={{ minWidth: 260 }}>
                  <input
                    type="search"
                    className="form-control form-control-sm"
                    placeholder="Search Short Description..."
                    value={subSearch}
                    onChange={(e) => setSubSearch(e.target.value)}
                  />
                  <i className="ti ti-search app-search-icon text-muted" />
                </div>
              </div>

              {subSearchTerm &&
                !(step3.rows || []).some((row, rowIndex) =>
                  (row.assetElementsData || []).some((ae) =>
                    (ae.mainElements || []).some((me) => mainElementHasMatch(mainKeyFor(rowIndex, ae.assetElementID, me)))
                  )
                ) && (
                  <div className="text-muted fst-italic py-3">
                    No sub-elements found matching "{subSearch.trim()}".
                  </div>
                )}

              {(step3.rows || []).map((row, rowIndex) => (
                <React.Fragment key={rowIndex}>
                  {(row.assetElementsData || []).map((ae) => {
                    const mainElements = ae.mainElements || [];

                    // Quick Search: skip this whole Asset Element group when
                    // searching and nothing under it matches at all.
                    if (
                      subSearchTerm &&
                      !mainElements.some((me) => mainElementHasMatch(mainKeyFor(rowIndex, ae.assetElementID, me)))
                    ) {
                      return null;
                    }

                    const visibleMainElements = subSearchTerm
                      ? mainElements.filter((me) => mainElementHasMatch(mainKeyFor(rowIndex, ae.assetElementID, me)))
                      : mainElements;

                    const activeMainKey = mainElements
                      .map((me) => mainKeyFor(rowIndex, ae.assetElementID, me))
                      .find((k) => expandedMain[k]);

                    return (
                      <div key={ae.assetElementID ?? ae.assetElementName} className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="mb-0 text-muted fs-sm fw-bold">
                            {row.assetGroupName ?? "Asset Group"} &gt; {ae.assetElementName ?? "Asset Element"} &gt; Main Element
                          </h6>

                          <div className="d-flex flex-column align-items-end gap-1">
                            {/* Per request, the per-Asset-Element "Cost of main
                                elements" line that used to sit here (one per
                                Asset Group/Asset Element section, computed from
                                assetElementAmount) was removed -- it read as
                                several different "Cost of main elements"
                                totals down the page. There is now exactly one
                                such total for the whole page, shown in the
                                section header above (see totalAmount /
                                getTotalAmount(), which already summed every
                                selected item across every Asset Group). */}
                            <fieldset disabled style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
                              <button
                                type="button"
                                className="btn btn-sm btn-dark text-uppercase px-3 me-2"
                                onClick={() => activeMainKey && selectAll(activeMainKey)}
                                disabled={!activeMainKey || loadingSubs[activeMainKey]}
                              >
                                Select All
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary text-uppercase px-3 me-2"
                                onClick={() => activeMainKey && uncheckAll(activeMainKey)}
                                disabled={!activeMainKey || loadingSubs[activeMainKey]}
                              >
                                Uncheck All
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-danger text-uppercase px-3"
                                onClick={() => activeMainKey && deleteSelected(activeMainKey)}
                                disabled={!activeMainKey || loadingSubs[activeMainKey]}
                              >
                                Remove
                              </button>
                            </fieldset>
                          </div>
                        </div>

                        <div className="sub-element-accordion border-top border-bottom">
                          {visibleMainElements.map((main) => {
                            const mainKey = mainKeyFor(rowIndex, ae.assetElementID, main);
                            // Quick Search: force this section open while
                            // there's a match to show (visibleMainElements
                            // already excludes non-matches), overriding
                            // expandedMain -- which is left untouched so
                            // clearing the search restores whatever was
                            // manually expanded before searching.
                            const isExpanded = subSearchTerm ? true : !!expandedMain[mainKey];
                            const subs = subElements[mainKey] || [];
                            const visibleSubs = subSearchTerm ? subs.filter(shortDescMatches) : subs;

                            // Per-Main-Element total (same Cost / Rate*Quantity
                            // fallback as getTotalAmount above, just scoped to
                            // this one mainKey instead of every selected row on
                            // the page) -- shown next to the Main Element's name
                            // as "Cost of main element = SAR ...". Currency is
                            // always "SAR" throughout this app (see the
                            // read-only Currency field in AddRateCardItemModal.jsx).
                            const mainSelected = selectedSubs[mainKey] || [];
                            const mainElementAmount = subs.reduce((sum, se) => {
                              if (!mainSelected.includes(se.SE_ID)) return sum;
                              return sum + (Number(se.Cost ?? (Number(se.Rate ?? 0) * Number(se.Quantity ?? 0))) || 0);
                            }, 0);

                            return (
                              <div key={main.ME_ID ?? main.Main_Element} className="border-bottom py-2">
                                <button
                                  type="button"
                                  // Per request, "px-0 py-1" (was "p-0") -- keeps
                                  // the same left/right alignment as before but
                                  // adds a little vertical padding, so the
                                  // highlight fill behind this row (see
                                  // themeOverrides.css's SUB ELEMENT ROW
                                  // HIGHLIGHT rule) reads as a slightly taller
                                  // bar instead of a thin strip hugging the text.
                                  className="btn btn-link w-100 text-start text-decoration-none d-flex justify-content-between align-items-center px-0 py-1"
                                  onClick={() => toggleExpand(rowIndex, ae.assetElementID, main)}
                                  disabled={loadingSubs[mainKey]}
                                >
                                  <strong className="fs-md text-green">{main.Main_Element ?? "Main Element"}</strong>

                                  {/* Right side, same spot as the page-wide
                                      "Amount" box that used to sit up in the
                                      section header -- now per Main Element
                                      instead of one combined page total. Per
                                      request, label and value are one line,
                                      same font size, instead of stacked. */}
                                  <div className="d-flex align-items-center gap-3">
                                    {/* Per request, bumped up from fs-sm (13px) to
                                        fs-lg (16px) to match the "Cost of main
                                        elements" span above -- both cost lines
                                        now read at the same, larger size. */}
                                    {/* Per request: no "Cost of sub elements ="
                                        label -- just the currency symbol
                                        (dynamic, from Currency Master via
                                        GET /currency above) and the value. */}
                                    <span className="text-end fs-lg">
                                      {currencySymbol}{" "}
                                      {mainElementAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>

                                    {loadingSubs[mainKey] ? (
                                      <span className="spinner-border spinner-border-sm text-muted" aria-hidden="true" />
                                    ) : (
                                      <i className={`ti fs-20 text-danger ${isExpanded ? "ti-minus" : "ti-plus"}`} />
                                    )}
                                  </div>
                                </button>

                                {isExpanded && (
                                  <fieldset disabled style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
                                  <div className="mt-2">
                                    <div className="table-responsive">
                                      {/* table-layout: fixed + the colgroup below pin every column
                                          to a fixed % width instead of letting each cell's content
                                          (e.g. a long Short Description option) resize the column.
                                          This table markup is shared by every Main Element group's
                                          accordion section (Sitework, test, etc.) -- since it's the
                                          same JSX rendered once per group, these widths apply
                                          identically everywhere, so every group's table now lines up
                                          the same way instead of drifting based on that group's own
                                          content. */}
                                      <table className="align-middle table" style={{ tableLayout: "fixed", width: "100%" }}>
                                        <colgroup>
                                          <col style={{ width: "4%" }} />
                                          <col style={{ width: "9%" }} />
                                          <col style={{ width: "17%" }} />
                                          <col style={{ width: "36%" }} />
                                          <col style={{ width: "6%" }} />
                                          <col style={{ width: "9%" }} />
                                          <col style={{ width: "7%" }} />
                                          <col style={{ width: "12%" }} />
                                        </colgroup>
                                        <thead>
                                          <tr className="fs-xs fw-bold text-success">
                                            <th></th>
                                            <th>Class</th>
                                            <th>Sub Element</th>
                                            <th>Short Description</th>
                                            <th>Unit</th>
                                            <th>Rate</th>
                                            <th>Quantity</th>
                                            <th>Cost</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {visibleSubs.map((se) => (
                                            <tr key={se.SE_ID}>
                                              <td>
                                                <input
                                                  type="checkbox"
                                                  className="form-check-input"
                                                  checked={(selectedSubs[mainKey] || []).includes(se.SE_ID)}
                                                  onChange={() => toggleSelectSub(mainKey, se.SE_ID)}
                                                />
                                              </td>
                                              <td>
                                                {se.asset_class ? (
                                                  <span className="badge bg-secondary-subtle text-secondary fw-semibold">
                                                    {se.asset_class}
                                                  </span>
                                                ) : (
                                                  <span className="text-muted">—</span>
                                                )}
                                              </td>
                                              <td>
                                                {/* bg-light added explicitly (rather than relying on the
                                                    browser's default :disabled styling) so every read-only
                                                    cell in this row -- and every row across every Main
                                                    Element group -- renders with the exact same flat gray
                                                    background, instead of varying by browser/Bootstrap
                                                    disabled-vs-readonly defaults (see Rate/Unit below). */}
                                                {/* Per request, the native dropdown-arrow icon is hidden --
                                                    this is a disabled, single-option display cell, not an
                                                    actual choosable dropdown, so the arrow was misleading. */}
                                                <select
                                                  className="form-select form-select-sm bg-light"
                                                  disabled
                                                  style={{ backgroundImage: "none", appearance: "none", WebkitAppearance: "none", MozAppearance: "none" }}
                                                >
                                                  <option>{se.Sub_Element ?? se.Short_Description ?? "—"}</option>
                                                </select>
                                              </td>
                                              <td>
                                                {/* Per request, a hover tooltip (native title attribute)
                                                    shows the full text -- this column is often truncated
                                                    by the column width, and a disabled <select> can't be
                                                    clicked open to read the rest another way. The native
                                                    dropdown-arrow icon is hidden for the same reason as the
                                                    Sub Element cell above -- it's a display-only cell. */}
                                                <select
                                                  className="form-select form-select-sm bg-light"
                                                  disabled
                                                  title={se.Short_Description ?? se.Description ?? ""}
                                                  style={{ backgroundImage: "none", appearance: "none", WebkitAppearance: "none", MozAppearance: "none" }}
                                                >
                                                  <option>{se.Short_Description ?? se.Description ?? "—"}</option>
                                                </select>
                                              </td>
                                              <td>
                                                {/* bg-light added -- plain readOnly inputs (unlike
                                                    disabled <select>s) keep a white background under
                                                    Bootstrap 5 by default, which is what made this row
                                                    look inconsistent with the Sub Element/Short
                                                    Description cells next to it. */}
                                                <input className="form-control form-control-sm bg-light" value={se.Unit ?? "—"} readOnly />
                                              </td>
                                              <td>
                                                <input
                                                  className="form-control form-control-sm bg-light"
                                                  readOnly
                                                  value={Number(se.Rate ?? 0).toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                  })}
                                                />
                                              </td>
                                              <td>
                                                <input
                                                  type="number"
                                                  min="0"
                                                  className="form-control form-control-sm"
                                                  value={se.Quantity}
                                                  onChange={(e) => updateQuantity(mainKey, se.SE_ID, e.target.value)}
                                                />
                                              </td>
                                              <td>
                                                <input
                                                  className="form-control form-control-sm bg-light"
                                                  readOnly
                                                  value={Number(se.Cost ?? 0).toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                  })}
                                                />
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                      {subs.length === 0 && (
                                        <div className="text-muted fst-italic py-2">No sub-elements loaded.</div>
                                      )}
                                    </div>

                                    <button
                                      type="button"
                                      className="btn btn-link text-decoration-none text-success fs-sm fw-semibold p-0"
                                      onClick={() => openAddItemModal(mainKey, row, ae, main)}
                                    >
                                      Add Item <i className="ti ti-circle-plus ms-1" />
                                    </button>
                                  </div>
                                  </fieldset>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}

              <div className="d-flex justify-content-between mt-3">
                <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(ROUTES.DASHBOARD, { state: { tab: "executive" } })}>
                  &larr; Back to Dashboard
                </button>
                <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(ROUTES.COST_ESTIMATION_SUMMARY)}>
                  &larr; Back to Summary
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AddRateCardItemModal
        show={addItemState.show}
        onClose={closeAddItemModal}
        onAdded={handleRateCardItemAdded}
        defaultCId={step3.C_ID}
        defaultAgId={addItemState.agId}
        defaultAgName={addItemState.agName}
        defaultAeId={addItemState.aeId}
        defaultAeName={addItemState.aeName}
        defaultMeId={addItemState.meId}
        defaultMeName={addItemState.meName}
      />

      {/* Themed "Cost item inserted successfully" confirmation -- same
          markup/colors as Step2RiskInflation.jsx's "Budget inserted
          successfully" popup, so both look consistent instead of one
          being a plain OS-styled "localhost:3000 says" box. */}
      {showSavedModal && (
        <div className="modal show d-block" tabIndex="-1" role="dialog" aria-modal="true" style={{ background: "rgba(0,0,0,0.75)" }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="border-0 modal-content text-white" style={{ backgroundColor: "#124d52", borderRadius: 4 }}>
              <div className="text-center modal-body p-4">
                <div className="mb-3">
                  <div className="align-items-center justify-content-center bg-white d-inline-flex rounded-circle" style={{ width: 48, height: 48 }}>
                    <i className="fs-2 text-dark ti ti-circle-check" style={{ fontStyle: "normal", fontWeight: 700 }} />
                  </div>
                </div>
                <h5 className="fs-5 fw-normal mb-4 modal-title">Cost item inserted successfully</h5>
                <div className="d-flex justify-content-center gap-3">
                  <button type="button" className="btn btn-primary" onClick={handleSavedModalOk}>
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Step4SubElementReadOnly;